import Stages from './Stages'
import { format } from 'date-fns'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui'
import Funnel from '@/components/funnel'
import { useQuery } from '@apollo/client'
import AudienceSettings from './Settings'
import { useParams } from 'react-router-dom'
import SettingsSelector from './SettingsSelector'
import ExclusionSettings from './ExclusionSettings'
import { useState, useRef, useEffect } from 'react'
import { showNotification } from '@/utils/notifications'
import { CubeLoader } from '@/components/ui/cube-loader'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { useAudienceDataWithHypotheses } from '@/hooks/useAudiencesWithHypotheses'
import { CalendarIcon, ResetIcon } from '@/components/icons'
import DateRangePicker from '@/components/ui/date-range-picker'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { useAudienceTargetingMetrics, getDefaultColumnPreferences } from './hooks'
import { getAvailableStages, getAllStages, isAudienceExcluded } from '@/utils/ad-campaigns-helpers'

const AudienceTargeting = () => {
    const { getCountrySettings, loadSettings, clearAudienceSettings } = useAudienceTargetingStore()
    const { getStepContentData, setStepContentData } = useWizard()
    const { id: campaignId } = useParams()

    const selectFormatData = getStepContentData('campaignObjective') || {}
    const salesFunnelSetupData = getStepContentData('salesFunnelSetup') || {}
    const audienceTargetingData = getStepContentData('audienceTargeting') || {}
    const locationTargetingData = getStepContentData('locationTargeting') || {}
    const selectedObjective = selectFormatData.selectedObjective?.toLowerCase() ?? 'awareness'
    const isWorldwideTargeting = locationTargetingData.selectedLocation === 'worldwide'

    const pixelIsActive = !!selectFormatData?.pixelIsValid
    const [audienceSelections, setAudienceSelections] = useState(salesFunnelSetupData.audienceSelections || {})
    const [selectedSettings, setSelectedSettings] = useState(audienceTargetingData.selectedSettings || 'default')
    const [selectedAudienceToEdit, setSelectedAudienceToEdit] = useState(null)
    const [selectedAudience, setSelectedAudience] = useState(null)
    const [hasScrolledToSettings, setHasScrolledToSettings] = useState(false)
    const mediaBuyingContext = useMediaBuyingContext()
    const { connectedPublishers } = mediaBuyingContext
    const publisher = connectedPublishers ? Object.values(connectedPublishers)[0] : null
    const currency = publisher?.currency
    const adAccountId = Object.values(connectedPublishers)[0].ad_account
    const [isDateRangePopoverOpen, setIsDateRangePopoverOpen] = useState(false)
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)

    // Initialize date range with default of last 30 days
    const [dateRange, setDateRange] = useState(() => {
        const getDefaultDateRange = () => {
            const endDate = new Date()
            const startDate = new Date()
            startDate.setDate(startDate.getDate() - 30)
            return {
                from: startDate,
                to: endDate,
            }
        }

        const from = audienceTargetingData?.dateRange?.from
        const to = audienceTargetingData?.dateRange?.to

        return {
            from: new Date(from || getDefaultDateRange().from),
            to: new Date(to || getDefaultDateRange().to),
        }
    })

    const { data: catalogueAudiencesData, loading: audiencesLoading } = useQuery(GET_AUDIENCES)
    const audiencesData = useAudienceDataWithHypotheses(catalogueAudiencesData, getStepContentData)
    const availableStages = getAvailableStages(selectedObjective)
    const allStages = getAllStages()
    // Filter out excluded audiences based on pixel status
    const allAudiences = audiencesData?.Audience?.audiences || []
    const audiences = allAudiences.filter((audience) => !isAudienceExcluded(audience, pixelIsActive))

    // Filter audience selections to remove any excluded audiences
    const filteredAudienceSelections = Object.keys(audienceSelections).reduce((acc, stageId) => {
        const stageAudiences = audienceSelections[stageId] || []
        const validAudiences = stageAudiences.filter((audienceId) => {
            const audience = allAudiences.find((a) => a.id === audienceId)
            return audience && !isAudienceExcluded(audience, pixelIsActive)
        })
        acc[stageId] = validAudiences
        return acc
    }, {})
    const audienceToEditRef = useRef(null)
    const settings = getCountrySettings()

    // Use custom hook for metrics management
    const { metricsLoading, refetchMetrics, formatMetricValue, fetchError, allMetricsData } =
        useAudienceTargetingMetrics({
            campaignId,
            adAccountId,
            dateRange,
            currency,
        })

    useEffect(() => {
        if (fetchError) {
            showNotification(fetchError.message || 'Failed to load metrics data', 'error', 5000, null, null, true, true)
        }
    }, [fetchError])

    // Update audience selections to use filtered ones and update wizard state if needed
    useEffect(() => {
        const hasFilteredOutAudiences =
            JSON.stringify(audienceSelections) !== JSON.stringify(filteredAudienceSelections)

        if (hasFilteredOutAudiences) {
            setAudienceSelections(filteredAudienceSelections)
            // Update the wizard state with filtered selections
            const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
            setStepContentData(
                {
                    ...salesFunnelData,
                    audienceSelections: filteredAudienceSelections,
                },
                'salesFunnelSetup',
            )
        }
    }, [pixelIsActive, allAudiences])

    useEffect(() => {
        if (audienceTargetingData.settings) loadSettings(audienceTargetingData.settings)
    }, [])

    useEffect(() => {
        setStepContentData({ selectedSettings, settings }, 'audienceTargeting')
    }, [settings, selectedSettings, setStepContentData])

    useEffect(() => {
        if (selectedAudienceToEdit && audienceToEditRef.current && !hasScrolledToSettings) {
            audienceToEditRef.current.scrollIntoView({ behavior: 'smooth' })
            setHasScrolledToSettings(true)
            setSelectedAudience(audiences.find((audience) => audience.id === selectedAudienceToEdit?.audienceId))
        }

        // Update selected audience when audiences data changes but don't scroll
        if (selectedAudienceToEdit && hasScrolledToSettings) {
            setSelectedAudience(audiences.find((audience) => audience.id === selectedAudienceToEdit?.audienceId))
        }
    }, [selectedAudienceToEdit, audiences, hasScrolledToSettings])

    const onSettingsSelectorChange = (value) => {
        setSelectedSettings(value)
        setSelectedAudienceToEdit(null)
        setHasScrolledToSettings(false)
    }

    const handleRemoveAudience = (stageId, audienceId) => {
        // Get current sales funnel data
        const salesFunnelData = getStepContentData('salesFunnelSetup') || {}

        // Remove the audience ID from this funnel stage
        const currentStageAudiences = filteredAudienceSelections[stageId] || []
        const updatedStageAudiences = currentStageAudiences.filter((id) => id !== audienceId)
        const updatedAudienceSelections = {
            ...filteredAudienceSelections,
            [stageId]: updatedStageAudiences,
        }

        // Update the wizard state
        setStepContentData(
            {
                ...salesFunnelData,
                audienceSelections: updatedAudienceSelections,
            },
            'salesFunnelSetup',
        )

        setAudienceSelections(updatedAudienceSelections)

        if (selectedAudienceToEdit?.audienceId === audienceId && selectedAudienceToEdit?.stageId === stageId) {
            setSelectedAudienceToEdit(null)
            setHasScrolledToSettings(false)
        }
    }

    const onDateRangeChange = (newRange) => {
        setIsDateRangePopoverOpen(false)
        setDateRange(newRange)

        setStepContentData(
            {
                ...audienceTargetingData,
                dateRange: newRange,
            },
            'audienceTargeting',
        )
    }

    const handleResetToDefaults = () => {
        if (selectedAudienceToEdit) setIsConfirmationDialogOpen(true)
    }

    const handleConfirmReset = () => {
        if (selectedAudienceToEdit) {
            // Clear all settings for the current audience being edited
            clearAudienceSettings(selectedAudienceToEdit.stageId, selectedAudienceToEdit.audienceId)

            // Reset selected settings to default
            setSelectedSettings('default')

            // Reset date range to default (last 30 days)
            const getDefaultDateRange = () => {
                const endDate = new Date()
                const startDate = new Date()
                startDate.setDate(startDate.getDate() - 30)
                return {
                    from: startDate,
                    to: endDate,
                }
            }
            const defaultDateRange = getDefaultDateRange()
            setDateRange(defaultDateRange)

            // Update the wizard state to reflect all reset values
            const updatedSettings = getCountrySettings()

            // Clear column preferences for all widgets to reset them to defaults
            const currentStepData = getStepContentData('audienceTargeting') || {}
            const defaultColumns = getDefaultColumnPreferences()

            const clearedStepData = {
                ...currentStepData,
                selectedSettings: 'default',
                settings: updatedSettings,
                dateRange: defaultDateRange,
                // Set column preferences to default metrics (spend and ROAS) for all widgets
                'columnPreferences-Gender': defaultColumns,
                'columnPreferences-Age': defaultColumns,
                'columnPreferences-Device': defaultColumns,
                'columnPreferences-PlatformPlacements': defaultColumns,
                'columnPreferences-Location': defaultColumns,
            }

            setStepContentData(clearedStepData, 'audienceTargeting')
        }

        setIsConfirmationDialogOpen(false)
    }

    const formatDateRange = () => {
        if (dateRange?.from && dateRange?.to)
            return `${format(dateRange.from, 'dd MMM yyyy')} - ${format(dateRange.to, 'dd MMM yyyy')}`

        return 'Set Period'
    }

    if (audiencesLoading) {
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" />
            </div>
        )
    }

    return (
        <div className="tw:flex tw:flex-col tw:h-full">
            <div className="tw:flex tw:h-full">
                <div className="tw:pt-6 tw:pb-14 tw:flex-1 tw:overflow-y-auto">
                    <span className="tw:text-sm tw:px-6 tw:block">
                        There are your selected audiences. All settings are automatically set. Click on an audience to
                        fine tune the parameters. You can always duplicate an audience to create more ad sets split
                        test.
                    </span>

                    <div className="tw:mt-6 tw:flex tw:gap-6 tw:flex-col">
                        <span className="tw:text-lg tw:font-medium tw:pl-6">Audience Settings</span>
                        <div className="tw:px-6">
                            <SettingsSelector selected={selectedSettings} onSelect={onSettingsSelectorChange} />
                        </div>
                        <div className="tw:px-6">
                            <ExclusionSettings
                                objective={selectedObjective}
                                getStepContentData={getStepContentData}
                                setStepContentData={setStepContentData}
                            />
                        </div>

                        {selectedSettings === 'manual' && (
                            <Stages
                                stages={allStages}
                                audiences={audiences}
                                availableStages={availableStages}
                                audienceSelections={filteredAudienceSelections}
                                selectedAudienceToEdit={selectedAudienceToEdit}
                                handleRemoveAudience={handleRemoveAudience}
                                handleEditAudience={(stageId, audienceId) => {
                                    setSelectedAudienceToEdit({ stageId, audienceId })
                                    setHasScrolledToSettings(false)
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className="tw:border-l tw:px-10 tw:py-6 tw:w-fit tw:flex-none tw:!h-full">
                    <Funnel availableStages={availableStages} audienceSelections={filteredAudienceSelections} />
                </div>
            </div>
            {selectedAudienceToEdit && (
                <div className="tw:flex tw:flex-col tw:gap-4.5 tw:border-t" ref={audienceToEditRef}>
                    <div className="tw:flex tw:justify-between tw:items-center  tw:px-6 tw:pt-6">
                        <div className="tw:flex tw:gap-1.5 tw:items-center">
                            <Settings className="tw:text-gray-200" />
                            <span className="tw:text-sm">{selectedAudience?.name} Audience Settings</span>
                        </div>
                        <div className="tw:flex tw:gap-2">
                            <Popover open={isDateRangePopoverOpen} onOpenChange={setIsDateRangePopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="link" className="tw:gap-0.5">
                                        <CalendarIcon className="tw:h-5 tw:w-5" />
                                        {formatDateRange()}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="tw:w-auto tw:p-0" align="start">
                                    <DateRangePicker
                                        value={dateRange}
                                        onChange={onDateRangeChange}
                                        onCancel={() => setIsDateRangePopoverOpen(false)}
                                    />
                                </PopoverContent>
                            </Popover>
                            <Button variant="link" className="tw:gap-0.5" onClick={handleResetToDefaults}>
                                <ResetIcon className="tw:h-5 tw:w-5" />
                                Reset to Defaults
                            </Button>
                        </div>
                    </div>
                    <div className="tw:px-6 tw:flex tw:flex-col tw:gap-6">
                        <AudienceSettings
                            stage={selectedAudienceToEdit.stageId}
                            audience={selectedAudienceToEdit.audienceId}
                            selectedAudience={selectedAudience}
                            isWorldwideTargeting={isWorldwideTargeting}
                            metricsLoading={metricsLoading}
                            refetchMetrics={refetchMetrics}
                            formatMetricValue={formatMetricValue}
                            fetchError={fetchError}
                            allMetricsData={allMetricsData}
                            getStepContentData={getStepContentData}
                            setStepContentData={setStepContentData}
                        />
                    </div>
                </div>
            )}
            <ConfirmationDialog
                cancelText="Cancel"
                variant="destructive"
                title="Reset to Defaults"
                confirmText="Reset Settings"
                onConfirm={handleConfirmReset}
                isOpen={isConfirmationDialogOpen}
                onClose={() => setIsConfirmationDialogOpen(false)}
                description={`Are you sure you want to reset all settings, selected columns, and date range for "${selectedAudience?.name}" audience to their default values?`}
            />
        </div>
    )
}

export default AudienceTargeting
